import React from "react";
import { Chip, Tooltip, Box, Typography } from "@mui/material";
import { lighten, darken } from "@mui/material/styles";
import { MergedHoliday } from "../types";
import {
  getColorForPill,
  getTextColorForPill,
  getStateColor,
  getStateTextColor,
} from "../constants/stateColors";
import { HolidayDetailsModal } from "./HolidayDetailsModal";

interface HolidayPillProps {
  holiday: MergedHoliday;
}

export const HolidayPill: React.FC<HolidayPillProps> = ({ holiday }) => {
  const [modalOpen, setModalOpen] = React.useState(false);

  const stateColor = getColorForPill(holiday.states);
  const textColor = getTextColorForPill(holiday.states);
  const label =
    holiday.type === "federal"
      ? holiday.holidayName
      : `${holiday.states.join(", ")}: ${holiday.holidayName}`;

  // Count sources
  const sourceCount = holiday.sources?.length || 1;

  // Determine background pattern
  const getBackgroundStyle = () => {
    const lighterColor = lighten(stateColor, 0.2);

    if (holiday.partDay && holiday.specialConditions) {
      // Both: combine striped and polkadot (striped will dominate)
      return {
        background: `repeating-linear-gradient(
          45deg,
          ${stateColor},
          ${stateColor} 4px,
          ${lighterColor} 4px,
          ${lighterColor} 8px
        )`,
        backgroundSize: undefined,
      };
    } else if (holiday.partDay) {
      // Striped background for part day
      return {
        background: `repeating-linear-gradient(
          45deg,
          ${stateColor},
          ${stateColor} 4px,
          ${lighterColor} 4px,
          ${lighterColor} 8px
        )`,
        backgroundSize: undefined,
      };
    } else if (holiday.specialConditions) {
      // Polkadot background for special conditions
      return {
        background: `radial-gradient(circle at 25% 25%, ${lighterColor} 2px, transparent 2px),
                    radial-gradient(circle at 75% 75%, ${lighterColor} 2px, transparent 2px),
                    ${stateColor}`,
        backgroundSize: "8px 8px",
      };
    }
    return {
      background: stateColor,
      backgroundSize: undefined,
    };
  };

  const { background: backgroundStyle, backgroundSize } = getBackgroundStyle();

  // For hover, darken the base color
  const getHoverBackgroundStyle = () => {
    const darkerColor = darken(stateColor, 0.15);
    const lighterColor = lighten(darkerColor, 0.2);

    if (holiday.partDay && holiday.specialConditions) {
      return `repeating-linear-gradient(
        45deg,
        ${darkerColor},
        ${darkerColor} 4px,
        ${lighterColor} 4px,
        ${lighterColor} 8px
      )`;
    } else if (holiday.partDay) {
      return `repeating-linear-gradient(
        45deg,
        ${darkerColor},
        ${darkerColor} 4px,
        ${lighterColor} 4px,
        ${lighterColor} 8px
      )`;
    } else if (holiday.specialConditions) {
      return `radial-gradient(circle at 25% 25%, ${lighterColor} 2px, transparent 2px),
              radial-gradient(circle at 75% 75%, ${lighterColor} 2px, transparent 2px),
              ${darkerColor}`;
    }
    return darkerColor;
  };

  const hoverBackgroundStyle = getHoverBackgroundStyle();

  // Tooltip content showing preview
  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      {/* Holiday Name */}
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, fontSize: "0.875rem" }}>
        {holiday.holidayName}
      </Typography>

      {/* Holiday Type */}
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, fontSize: "0.65rem", color: "rgba(255,255,255,0.7)" }}
        >
          TYPE
        </Typography>
        <Typography variant="caption" sx={{ fontSize: "0.85rem", display: "block", mt: 0.25 }}>
          {holiday.type.charAt(0).toUpperCase() + holiday.type.slice(1)}
        </Typography>
      </Box>

      {/* special conditions - if any */}
      {(holiday.partDay || holiday.specialConditions) && (
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, fontSize: "0.65rem", color: "rgba(255,255,255,0.7)" }}
          >
            CONDITIONS
          </Typography>
          {holiday.partDay && (
            <Typography
              variant="caption"
              sx={{ fontSize: "0.85rem", display: "block", mt: 0.25 }}
            >
              Part Day Holiday
            </Typography>
          )}
          {holiday.specialConditions && (
            <Typography
              variant="caption"
              sx={{ fontSize: "0.85rem", display: "block", mt: 0.25 }}
            >
              {holiday.specialConditions}
            </Typography>
          )}
        </Box>
      )}

      {/* Applies To */}
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, fontSize: "0.65rem", color: "rgba(255,255,255,0.7)" }}
        >
          APPLIES TO
        </Typography>
        <Box sx={{ mt: 0.5, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {holiday.type === "federal" ? (
            <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
              Federal
            </Typography>
          ) : (
            holiday.states.map((state) => (
              <Box
                key={state}
                sx={{
                  px: 0.75,
                  py: 0.25,
                  bgcolor: getStateColor(state),
                  color: getStateTextColor(state),
                  borderRadius: "4px",
                  fontSize: "0.65rem",
                  fontWeight: 500,
                }}
              >
                {state}
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* Date */}
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, fontSize: "0.65rem", color: "rgba(255,255,255,0.7)" }}
        >
          DATE
        </Typography>
        <Typography variant="caption" sx={{ fontSize: "0.85rem", display: "block", mt: 0.25 }}>
          {/* {holiday.dateString} */}
          {`${holiday.dateString.slice(0, 4)}-${holiday.dateString.slice(4, 6)}-${holiday.dateString.slice(6, 8)}`}
        </Typography>
      </Box>

      {/* Information */}
      {holiday.information && (
        <Box>
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, fontSize: "0.65rem", color: "rgba(255,255,255,0.7)" }}
          >
            INFO
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontSize: "0.65rem", display: "block", mt: 0.25, maxWidth: 250 }}
          >
            {holiday.information}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Tooltip title={tooltipContent} enterDelay={500} arrow>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            mb: 0.5,
          }}
        >
          <Chip
            label={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  pr: 0.5,
                }}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {label}
                </span>

                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 0.5,
                    py: 0.25,
                    bgcolor: "rgba(0,0,0,0.2)",
                    borderRadius: "4px",
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    minWidth: "18px",
                    textAlign: "center",
                    flexShrink: 0,
                  }}
                >
                  {sourceCount}
                </Box>
              </Box>
            }
            size="small"
            sx={{
              width: "100%",
              fontSize: "0.75rem",
              height: "24px",
              cursor: "pointer",
              background: backgroundStyle,
              backgroundSize: backgroundSize,
              color: textColor,
              fontWeight: holiday.type === "federal" ? 700 : 500,
              justifyContent: "flex-start",
              "&:hover": {
                background: hoverBackgroundStyle,
                backgroundSize: backgroundSize,
              },
              "& .MuiChip-label": {
                width: "100%",
                paddingLeft: "8px",
                paddingRight: "8px",
                display: "flex",
              },
            }}
            onClick={() => setModalOpen(true)}
          />
        </Box>
      </Tooltip>
      <HolidayDetailsModal
        open={modalOpen}
        holiday={holiday}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};
